import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { setAdFreeManualSubscriber } from '../stores/adFreeSubscriberStore';

/**
 * The app has no in-app purchases. Ad-free status comes only from the
 * admin-managed `users.ad_free_manual` flag; ebooks unlock via a rewarded ad.
 */
type Value = {
  isAdFreeManual: boolean;
  isPremiumSubscriber: boolean;
  ready: boolean;
  refresh: () => Promise<void>;
};

const AdFreeSubscriberContext = createContext<Value | null>(null);

export function AdFreeSubscriberProvider({ children }: { children: React.ReactNode }) {
  const [isAdFreeManual, setIsAdFreeManual] = useState(false);
  const [ready, setReady] = useState(false);

  const refreshManualFlag = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      setIsAdFreeManual(false);
      setAdFreeManualSubscriber(false);
      return;
    }
    const { data, error } = await supabase
      .from('users')
      .select('ad_free_manual')
      .eq('id', session.user.id)
      .maybeSingle();
    const next = !error && !!data?.ad_free_manual;
    setIsAdFreeManual(next);
    setAdFreeManualSubscriber(next);
  }, []);

  const refresh = useCallback(async () => {
    setReady(false);
    await refreshManualFlag();
    setReady(true);
  }, [refreshManualFlag]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED') return;
      void refresh();
    });
    const onFocus = () => void refresh();
    const onVis = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refresh]);

  const value = useMemo<Value>(
    () => ({
      isAdFreeManual,
      isPremiumSubscriber: isAdFreeManual,
      ready,
      refresh,
    }),
    [isAdFreeManual, ready, refresh]
  );

  return <AdFreeSubscriberContext.Provider value={value}>{children}</AdFreeSubscriberContext.Provider>;
}

export function useAdFreeSubscriber(): Value {
  const ctx = useContext(AdFreeSubscriberContext);
  if (!ctx) {
    throw new Error('useAdFreeSubscriber must be used within AdFreeSubscriberProvider');
  }
  return ctx;
}
